import { useSyncExternalStore } from 'react';

const maxWidthForBreakpoint = {
    xs: 376,
    sm: 599,
    md: 1024,
    lg: 1239,
    xl: 1444,
    xxl: 1640,
    customBracket: 1490,
};

type BreakpointEntry = {
    subscribe: (cb: () => void) => () => void;
    getSnapshot: () => boolean;
    getServerSnapshot: () => boolean;
};

const entries = new Map<number, BreakpointEntry>();

function getEntry(maxWidth: number): BreakpointEntry {
    let entry = entries.get(maxWidth);
    if (entry) return entry;

    const listeners = new Set<() => void>();
    let currentValue = false;

    if (typeof window !== 'undefined') {
        const mql = window.matchMedia(`(max-width: ${maxWidth}px)`);
        currentValue = mql.matches;
        mql.addEventListener('change', (e) => {
            currentValue = e.matches;
            listeners.forEach((l) => l());
        });
    }

    entry = {
        subscribe: (cb: () => void) => {
            listeners.add(cb);
            return () => {
                listeners.delete(cb);
            };
        },
        getSnapshot: () => currentValue,
        getServerSnapshot: () => false,
    };

    entries.set(maxWidth, entry);
    return entry;
}

function useBreakpointDown(maxWidth: number): boolean {
    const { subscribe, getSnapshot, getServerSnapshot } = getEntry(maxWidth);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

const downDict = Object.fromEntries(
    Object.entries(maxWidthForBreakpoint).map(([k, v]) => [k, `@media (max-width: ${v}px)`]),
) as { [breakpoint in keyof typeof maxWidthForBreakpoint]: string };

const mediaQueryDownDict = Object.fromEntries(
    Object.entries(maxWidthForBreakpoint).map(([k, v]) => [k, `(max-width: ${v}px)`]),
) as { [breakpoint in keyof typeof maxWidthForBreakpoint]: string };

// Flat, `use`-prefixed properties directly on the default export — not nested under an
// `isWidthDown` sub-object — so each call site (`Breakpoint.useIsWidthDownSm()`) is a
// two-level `PascalCaseNamespace.useXxx()` member expression, the only shape React
// Compiler's hook-detection heuristic recognizes for a hook hidden behind property access
// (see `isHook` in babel-plugin-react-compiler). The previous three-level
// `Breakpoint.isWidthDown.sm()` shape was invisible to the compiler, which then silently
// treated these hook calls as ordinary memoizable function calls and could skip/reorder
// them between renders — corrupting hook call order at runtime once a calling component
// (e.g. Navbar) was actually compiled.
export default {
    useIsWidthDownXs: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xs),
    useIsWidthDownSm: (): boolean => useBreakpointDown(maxWidthForBreakpoint.sm),
    useIsWidthDownMd: (): boolean => useBreakpointDown(maxWidthForBreakpoint.md),
    useIsWidthDownLg: (): boolean => useBreakpointDown(maxWidthForBreakpoint.lg),
    useIsWidthDownXl: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xl),
    useIsWidthDownXxl: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xxl),
    useIsWidthDownCustomBracket: (): boolean => useBreakpointDown(maxWidthForBreakpoint.customBracket),
    useIsWidthDownCustom: (maxWidth: number): boolean => useBreakpointDown(maxWidth),
    down: downDict,
    mediaQuery: {
        down: mediaQueryDownDict,
    },
};
